import { GlobeIcon, WarningIcon, XIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { siteCheckImageSrc, siteCheckStatusVariant, type SiteCheckOutput } from './site-check-output';

/**
 * FRONT-1992: the chat card for a `site_check_page` result — a screenshot of
 * one page of the person's own website, taken at their configured preview
 * address. Same click-to-enlarge shape as `GeneratedImageCard`, plus an HTTP
 * status badge (`GeneratedImageCard` has no status to show; this tool always
 * does) — CLAUDE.md's one-green-one-red tokens, never a raw Tailwind colour.
 *
 * `data-testid="site-check-card"` on the outer element for a browser spec to
 * find this over the generic `ToolCallCard` fallback.
 */
export function SiteCheckCard({
  output,
  label = 'Page check',
}: {
  output: SiteCheckOutput;
  /** FRONT-1724: `browser_screenshot` reuses this card as "Screenshot"; its
   *  result carries no path or HTTP status, so neither renders. */
  label?: string;
}) {
  const src = siteCheckImageSrc(output);

  if (output.error || !src) {
    return (
      <div
        data-testid="site-check-card"
        className="flex items-center gap-2 text-xs text-destructive px-3 py-2 border border-destructive/20 rounded-lg"
      >
        <WarningIcon size={14} />
        <span>{output.error ?? 'Could not check that page.'}</span>
      </div>
    );
  }

  const statusVariant = siteCheckStatusVariant(output.status);
  const isPageCheck = label === 'Page check';
  const path = output.path ?? (isPageCheck ? '/' : undefined);
  const alt = isPageCheck ? `${output.title || path} — website check` : `${output.title || 'A web page'} — screenshot`;

  return (
    <div data-testid="site-check-card" className="border border-border rounded-lg overflow-hidden bg-card max-w-[420px]">
      <div className="px-3 py-2 flex items-center gap-2 text-xs border-b border-border">
        <GlobeIcon size={14} className="text-muted-foreground shrink-0" />
        <span className="font-medium text-foreground truncate">{label}</span>
        {path && <span className="font-mono text-muted-foreground truncate">{path}</span>}
        {statusVariant && (
          <Badge variant={statusVariant} className="ml-auto shrink-0">
            HTTP {output.status}
          </Badge>
        )}
      </div>
      <Dialog>
        <DialogTrigger asChild>
          <button type="button" className="group block w-full cursor-pointer">
            <img
              src={src}
              alt={alt}
              loading="lazy"
              className="block w-full h-auto bg-muted/30 outline outline-1 -outline-offset-1 outline-border transition-opacity group-hover:opacity-90"
            />
          </button>
        </DialogTrigger>
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          className="w-fit sm:max-w-[calc(100%-2rem)] gap-0 overflow-hidden p-0"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Website check preview</DialogTitle>
          </DialogHeader>
          <div className="relative flex h-[82vh] max-h-[720px] w-[88vw] max-w-[960px] items-center justify-center bg-muted/30">
            <img
              src={src}
              alt={alt}
              className="h-full w-full object-contain outline outline-1 -outline-offset-1 outline-border"
            />
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon-lg"
                className="absolute top-2 right-2 rounded-full bg-black/50 text-white transition-[background-color,transform] hover:bg-black/70 hover:text-white active:scale-[0.96]"
              >
                <XIcon size={16} />
                <span className="sr-only">Close</span>
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
      {output.title && (
        <div className="px-3 py-2 border-t border-border">
          <p className="text-xs text-muted-foreground truncate">{output.title}</p>
        </div>
      )}
    </div>
  );
}
